import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { NativeSelect } from '@/components/form';
import { MenuLink } from '@/components/menu-link';
import { EmptyState, PageHeader } from '@/components/page-header';
import { ExpensesMoneyBar } from '@/components/purchases/purchase-money-bar';
import { PurchaseTable } from '@/components/purchases/purchase-table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useMe } from '@/lib/clients';
import { PERIODS, periodStart } from '@/lib/dates';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { usePurchaseTransactions, type PurchaseRow } from '@/lib/purchases';

export const Route = createFileRoute('/expenses/')({ component: Expenses });

const TYPES = ['all', 'bill', 'expense', 'vendor_payment', 'debit_note'] as const;
/** Payee filter value for expenses without a payee. */
const NO_PAYEE = 'none';
const open = (r: PurchaseRow) => ['approved', 'partially_paid'].includes(r.status);
const STATUSES: Array<
  ['all' | 'open' | 'overdue' | 'draft' | 'paid' | 'void', (r: PurchaseRow) => boolean]
> = [
  ['all', () => true],
  ['open', open],
  ['overdue', (r) => r.overdue],
  ['draft', (r) => r.status === 'draft'],
  ['paid', (r) => ['paid', 'posted'].includes(r.status)],
  ['void', (r) => r.status === 'void'],
];
const BAR: Record<string, (r: PurchaseRow) => boolean> = {
  spend: (r) =>
    ['bill', 'expense', 'debit_note'].includes(r.type) && !['draft', 'void'].includes(r.status),
  overdue: (r) => r.type === 'bill' && open(r) && r.overdue,
  not_due: (r) => r.type === 'bill' && open(r) && !r.overdue,
  draft: (r) => r.status === 'draft',
  credit: (r) =>
    (r.type === 'debit_note' && open(r)) ||
    (r.type === 'vendor_payment' && r.status === 'posted' && r.balance !== '0.00'),
};

/** Expenses (document 06 E1): bills, expenses, bill payments and debit notes with filters. */
function Expenses() {
  const t = useT();
  const orgId = useOrgId();
  const me = useMe();
  const tx = usePurchaseTransactions();
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [period, setPeriod] = useState('all');
  const [payee, setPayee] = useState('all');
  const [q, setQ] = useState('');
  const [bar, setBar] = useState<string | null>(null);
  const payees = useMemo(
    () =>
      [
        ...new Map(
          (tx.data ?? []).map((r) => [
            r.contactId ?? NO_PAYEE,
            r.contactName ?? t('purchasePages.expensesHome.noPayee'),
          ]),
        ).entries(),
      ].sort((a, b) => a[1].localeCompare(b[1])),
    [tx.data, t],
  );
  const since = periodStart(period);
  const needle = q.trim().toLowerCase();
  const statusFn = STATUSES.find((s) => s[0] === status)![1];
  const rows = (tx.data ?? []).filter(
    (r) =>
      (type === 'all' || r.type === type) &&
      statusFn(r) &&
      (!since || r.date >= since) &&
      (payee === 'all' || (r.contactId ?? NO_PAYEE) === payee) &&
      (!bar || BAR[bar]!(r)) &&
      (!needle ||
        (r.contactName ?? '').toLowerCase().includes(needle) ||
        (r.number ?? '').toLowerCase().includes(needle) ||
        (r.reference ?? '').toLowerCase().includes(needle) ||
        (r.category ?? '').toLowerCase().includes(needle) ||
        (r.memo ?? '').toLowerCase().includes(needle)),
  );
  return (
    <>
      <PageHeader
        title={t('purchasePages.expensesHome.title')}
        actions={
          me.data?.isStaff ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="gap-1">
                  {t('purchasePages.expensesHome.newTransaction')}{' '}
                  <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <MenuLink to="/expenses/expenses/new">
                  {t('purchasePages.expensesHome.menuExpense')}
                </MenuLink>
                <MenuLink to="/expenses/bills/new">
                  {t('purchasePages.expensesHome.menuBill')}
                </MenuLink>
                <MenuLink to="/expenses/pay-bills">
                  {t('purchasePages.expensesHome.menuPayBills')}
                </MenuLink>
                <MenuLink to="/expenses/debit-notes/new">
                  {t('purchasePages.expensesHome.menuDebitNote')}
                </MenuLink>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null
        }
      />
      {!orgId ? (
        <EmptyState
          title={t('shell.selectClient')}
          hint={t('purchasePages.listCommon.selectClientHint')}
        />
      ) : (
        <>
          <ExpensesMoneyBar active={bar} onSelect={setBar} />
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <NativeSelect
              aria-label={t('purchasePages.expensesHome.typeLabel')}
              className="w-[180px] bg-card"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {TYPES.map((v) => (
                <option key={v} value={v}>
                  {t(`purchasePages.expensesHome.types.${v}`)}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label={t('purchasePages.listCommon.searchStatus')}
              className="w-[160px] bg-card"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUSES.map(([v]) => (
                <option key={v} value={v}>
                  {t(`purchasePages.expensesHome.statuses.${v}`)}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label={t('purchasePages.listCommon.date')}
              className="w-[160px] bg-card"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {PERIODS.map(([v]) => (
                <option key={v} value={v}>
                  {t(`purchasePages.listCommon.dateFilter.${v as '30'}`)}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label={t('purchasePages.expensesHome.payeeLabel')}
              className="w-[220px] bg-card"
              value={payee}
              onChange={(e) => setPayee(e.target.value)}
            >
              <option value="all">{t('purchasePages.expensesHome.allPayees')}</option>
              {payees.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </NativeSelect>
            <div className="relative w-[300px]">
              <Search className="pointer-events-none absolute start-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                className="bg-card ps-8"
                placeholder={t('purchasePages.expensesHome.searchPlaceholder')}
                aria-label={t('purchasePages.expensesHome.searchLabel')}
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
          {tx.isPending ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : tx.isError ? (
            <EmptyState title={t('purchasePages.expensesHome.loadError')} hint={tx.error.message} />
          ) : rows.length === 0 ? (
            <EmptyState
              title={
                tx.data.length
                  ? t('purchasePages.expensesHome.noMatch')
                  : t('purchasePages.expensesHome.empty')
              }
              hint={t('purchasePages.expensesHome.emptyHint')}
            />
          ) : (
            <PurchaseTable rows={rows} />
          )}
        </>
      )}
    </>
  );
}
